import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type WorkflowState = 'draft' | 'submitted' | 'approved' | 'rejected';

interface ApprovalDotProps {
  state: WorkflowState;
  onClick?: (e: React.MouseEvent) => void;
  size?: number;
  title?: string;
}

const STATE_COLOR: Record<WorkflowState, string> = {
  draft: '#9ca3af',
  submitted: '#eab308',
  approved: '#22c55e',
  rejected: '#ef4444',
};

const STATE_LABEL: Record<WorkflowState, string> = {
  draft: 'Draft - not submitted',
  submitted: 'Submitted — pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

const ApprovalDot: React.FC<ApprovalDotProps> = ({ state, onClick, size = 8, title }) => {
  const color = STATE_COLOR[state];
  const label = title || STATE_LABEL[state];
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const handleMouseEnter = () => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.top + window.scrollY - 6,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  };

  const handleMouseLeave = () => setPos(null);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
        aria-label={label}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: color,
          border: `1px solid ${color}`,
          boxShadow: `0 0 0 2px ${color}22`,
          cursor: onClick ? 'pointer' : 'default',
          padding: 0,
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      />
      {pos && createPortal(
        <div
          style={{
            position: 'absolute',
            top: pos.top,
            left: pos.left,
            transform: 'translate(-50%, -100%)',
            background: '#111827',
            color: '#fff',
            fontSize: 11,
            fontWeight: 500,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            padding: '4px 8px',
            borderRadius: 4,
            pointerEvents: 'none',
            zIndex: 99999,
          }}
        >
          {label}
        </div>,
        document.body
      )}
    </>
  );
};

export default ApprovalDot;
